import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FileText, Calendar, ChevronRight, ClipboardList,
  CheckCircle, XCircle, Clock,
} from 'lucide-react'
import { useStore } from '@/contexts/StoreContext'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from '@/contexts/ThemeContext'
import { formService, Form, MySubmission } from '@/lib/services/form.service'
import PageHero from '@/components/common/PageHero'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Caixa } from '@/construtor/caixa'

const statusConfig = {
  PENDING:  { label: 'Pendente',  icon: Clock,         className: 'bg-warning/10 text-warning border-warning/30' },
  APPROVED: { label: 'Aprovado',  icon: CheckCircle,   className: 'bg-success/10 text-success border-success/30' },
  REJECTED: { label: 'Recusado',  icon: XCircle,       className: 'bg-destructive/10 text-destructive border-destructive/30' },
}

export default function FormsPage() {
  const navigate = useNavigate()
  const { store } = useStore()
  const { isAuthenticated, openLoginModal } = useAuth()
  const { config } = useTheme()

  const [tab, setTab] = useState<'forms' | 'submissions'>('forms')
  const [forms, setForms] = useState<Form[]>([])
  const [submissions, setSubmissions] = useState<MySubmission[]>([])
  const [loadingForms, setLoadingForms] = useState(true)
  const [loadingSubmissions, setLoadingSubmissions] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (store?.id) loadForms()
  }, [store])

  useEffect(() => {
    if (tab === 'submissions' && isAuthenticated && store?.id) loadSubmissions()
  }, [tab, isAuthenticated, store])

  async function loadForms() {
    if (!store?.id) return
    try {
      setLoadingForms(true)
      setError(null)
      const data = await formService.listActiveForms(store.id)
      setForms(data.forms)
    } catch (err: any) {
      setError(err?.message || 'Erro ao carregar formulários')
    } finally {
      setLoadingForms(false)
    }
  }

  async function loadSubmissions() {
    if (!store?.id) return
    try {
      setLoadingSubmissions(true)
      const data = await formService.getMySubmissions(store.id)
      setSubmissions(data.submissions)
    } catch {
      setSubmissions([])
    } finally {
      setLoadingSubmissions(false)
    }
  }

  return (
    <div>
      <PageHero
        title="Formulários"
        breadcrumbs={[{ label: 'Formulários' }]}
      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <Caixa id="formularios.abas">
        <div className="flex gap-1 mb-6 bg-muted/40 border border-border rounded-xl p-1 w-fit">
          <button
            onClick={() => setTab('forms')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === 'forms'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <span className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Formulários
            </span>
          </button>
          <button
            onClick={() => setTab('submissions')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === 'submissions'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <span className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              Minhas Respostas
            </span>
          </button>
        </div>
        </Caixa>

        {/* Forms Tab */}
        {tab === 'forms' && (
          loadingForms ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
            </div>
          ) : error ? (
            <Caixa id="formularios.aviso" miolo>
            <Card className="border-border">
              <CardContent className="p-12 flex flex-col items-center text-center gap-4">
                <div className="w-20 h-20 rounded-2xl bg-destructive/10 flex items-center justify-center">
                  <ClipboardList className="w-10 h-10 text-destructive" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground mb-2">Erro ao carregar formulários</h3>
                  <p className="text-muted-foreground">{error}</p>
                </div>
              </CardContent>
            </Card>
            </Caixa>
          ) : forms.length === 0 ? (
            <Caixa id="formularios.vazio" miolo>
            <Card className="border-border">
              <CardContent className="p-12 flex flex-col items-center text-center gap-4">
                <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <ClipboardList className="w-10 h-10 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground mb-2">Nenhum formulário disponível</h3>
                  <p className="text-muted-foreground">Não há formulários abertos no momento. Volte em breve!</p>
                </div>
              </CardContent>
            </Card>
            </Caixa>
          ) : (
            <Caixa id="formularios.lista">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {forms.map((form) => (
                <Caixa id="formularios.item" repetida key={form.id}>
                <Card
                  key={form.id}
                  className="border-border hover:border-primary/50 transition-all duration-200 cursor-pointer group"
                  onClick={() => navigate(`/formularios/${form.slug}`)}
                >
                  <CardContent className="p-6 flex flex-col gap-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                        <FileText className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex gap-2 flex-wrap justify-end">
                        <Badge className="bg-success text-success-foreground text-xs">Aberto</Badge>
                        {form.reapplyable && (
                          <Badge variant="outline" className="text-xs">Reaplicável</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground text-lg leading-tight mb-1 group-hover:text-primary transition-colors">
                        {form.title}
                      </h3>
                      {form.description && (
                        <p className="text-muted-foreground text-sm line-clamp-2">
                          {form.description.replace(/<[^>]+>/g, '').substring(0, 120)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {form.endsAt ? (
                          <span>Encerra {formatDistanceToNow(new Date(form.endsAt), { addSuffix: true, locale: ptBR })}</span>
                        ) : (
                          <span>Sem prazo definido</span>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 group-hover:text-primary transition-colors" />
                    </div>
                  </CardContent>
                </Card>
                </Caixa>
              ))}
            </div>
            </Caixa>
          )
        )}

        {/* Submissions Tab */}
        {tab === 'submissions' && (
          !isAuthenticated ? (
            <Caixa id="formularios.respostas.aviso" miolo>
            <Card className="border-border">
              <CardContent className="p-12 flex flex-col items-center text-center gap-4">
                <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <ClipboardList className="w-10 h-10 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground mb-2">Login necessário</h3>
                  <p className="text-muted-foreground">Faça login para ver seus formulários respondidos.</p>
                </div>
                <Button onClick={openLoginModal}>Entrar</Button>
              </CardContent>
            </Card>
            </Caixa>
          ) : loadingSubmissions ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
            </div>
          ) : submissions.length === 0 ? (
            <Caixa id="formularios.respostas.vazio" miolo>
            <Card className="border-border">
              <CardContent className="p-12 flex flex-col items-center text-center gap-4">
                <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <ClipboardList className="w-10 h-10 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground mb-2">Nenhuma resposta</h3>
                  <p className="text-muted-foreground">Você ainda não respondeu nenhum formulário.</p>
                </div>
                <Button variant="outline" onClick={() => setTab('forms')}>Ver formulários</Button>
              </CardContent>
            </Card>
            </Caixa>
          ) : (
            <Caixa id="formularios.respostas">
            <div className="space-y-4 max-w-2xl">
              {submissions.map((sub) => {
                const cfg = statusConfig[sub.status] ?? statusConfig.PENDING
                const Icon = cfg.icon
                return (
                  <Caixa id="formularios.respostas.item" repetida key={sub.id}>
                  <Card
                    key={sub.id}
                    className="border-border hover:border-primary/40 transition-all cursor-pointer group"
                    onClick={() => navigate(`/minhas-respostas/${sub.id}`)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                          <ClipboardList className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors leading-tight">
                              {sub.formTitle}
                            </h3>
                            <Badge className={`border flex-shrink-0 text-xs px-2 py-0.5 ${cfg.className}`}>
                              <Icon className="w-3 h-3 mr-1" />
                              {cfg.label}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                            <Calendar className="w-3 h-3" />
                            <span>
                              Respondido {formatDistanceToNow(new Date(sub.submittedAt), { addSuffix: true, locale: ptBR })}
                            </span>
                          </div>
                          {sub.observation && (
                            <div className={`mt-2 p-3 rounded-lg border text-sm ${cfg.className}`}>
                              <p className="font-medium mb-0.5">Observação:</p>
                              <p className="opacity-90">{sub.observation}</p>
                            </div>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 mt-1" />
                      </div>
                    </CardContent>
                  </Card>
                  </Caixa>
                )
              })}
            </div>
            </Caixa>
          )
        )}
      </div>
    </div>
  )
}
